import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cnMerge } from '../../utils/twmerge'
import type { SimpleSelectProps, SelectOption } from './types'

export const SimpleSelect: React.FC<SimpleSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione uma opção...",
  className,
  disabled = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    options.find(opt => opt.value === value) || null
  )
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 })
  const selectRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Sincronizar selectedOption quando value mudar externamente
  useEffect(() => {
    const newSelectedOption = options.find(opt => opt.value === value) || null
    setSelectedOption(newSelectedOption)
  }, [value, options])

  // Calcular posição do dropdown quando aberto
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      const dropdownHeight = Math.min(240, options.length * 40 + 8) // max-h-60 ≈ 240px
      const spaceBelow = viewport.height - rect.bottom
      const spaceAbove = rect.top
      
      let top = rect.bottom + window.scrollY + 4 // 4px gap (mt-1)
      
      // Se não há espaço suficiente abaixo, mostrar acima
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4
      }
      
      setDropdownCoords({
        top,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [isOpen, options.length])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    
    return () => {} // Return vazio quando não há listener
  }, [isOpen])

  const handleOptionClick = (option: SelectOption) => {
    if (option.disabled) return
    
    setSelectedOption(option)
    onChange?.(option.value)
    setIsOpen(false)
  }

  return (
    <>
      <div className={cnMerge("relative", className)} ref={selectRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cnMerge(
            // Base styles
            'flex h-8 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm',
            // Normal border styles
            !error && 'border-gray-300 dark:border-neutral-600',
            // Error border styles
            error && 'border-red-500 dark:border-red-500',
            // Dark mode
            'dark:bg-neutral-800 dark:text-gray-100',
            // Focus styles
            'focus-visible:outline-none focus-visible:ring-2',
            !error && 'focus-visible:ring-blue-500/20 focus-visible:border-blue-500',
            error && 'focus-visible:ring-red-500/20 focus-visible:border-red-500',
            // Transition
            'transition-colors duration-200',
            // Disabled styles
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-neutral-700',
            // Hover
            !error && 'hover:border-gray-400 dark:hover:border-neutral-500',
            error && 'hover:border-red-600 dark:hover:border-red-400',
            // Open state
            isOpen && !error && 'border-blue-500 ring-2 ring-blue-500/20',
            isOpen && error && 'border-red-500 ring-2 ring-red-500/20'
          )}
        >
          <span className={cnMerge(
            selectedOption ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
          )}>
            {selectedOption?.label || placeholder}
          </span>
          <svg
            className={cnMerge(
              'h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown usando Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-lg"
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`
          }}
        >
          <div className="max-h-60 overflow-auto py-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleOptionClick(option)}
                disabled={option.disabled}
                className={cnMerge(
                  'flex w-full items-center px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-neutral-700 focus:bg-gray-50 dark:focus:bg-neutral-700 focus:outline-none',
                  'text-gray-900 dark:text-gray-100',
                  option.disabled && 'cursor-not-allowed opacity-50',
                  selectedOption?.value === option.value && 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
